// tables/E2ETable.tsx
import React, { useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table'
import ExportTable from '../../../../../components/portal/export/ExportTable'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../redux'
import useAuth from '../../../../../hooks/useAuth'
import { createExport, createExportObject, createExportRows } from '../../../../../lib/general/export'
import { Pagination } from '../../../../../components/ui/pagination'
import { splitArrayIntoChunks } from '../../../../../lib/utils'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { formatDateToYYYYMMDD } from '../../../../../lib/date'

type Props = {
  services: any[]
  selectedPartnerId?: string | null
  selectedPartnerName?: string | null
  selectedCustomerId?: string | null
  selectedCustomerName?: string | null
}

const E2ETable: React.FC<Props> = ({
  services,
  selectedPartnerId,
  selectedPartnerName,
  selectedCustomerId,
  selectedCustomerName
}) => {
  const { auth } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const partners = useSelector((state: RootState) => state.partners)
  const customers = useSelector((state: RootState) => state.unfilteredCustomers)
  const [currentPage, setcurrentPage] = useState(1)

  const headerArr = [
    t("sip.o_e2e.modified"),
    t("sip.o_e2e.partner"),
    t("sip.o_e2e.customer"),
    t("sip.o_e2e.atm_id"),
    t("sip.o_e2e.date"),
    t("sip.o_e2e.creation_date"),
  ]

  // Group by clientcode + date to show unique rows
  const uniqueServices = Array.from(
    services.reduce((map, service) => {
      const key = `${service.m_ClientID}_${service.m_DATUM}_${service.m_RELNR}`
      if (!map.has(key)) map.set(key, service)
      return map
    }, new Map())
    .values()
  )

  return (
    <>
      {auth.data?.right.export && (
        <div className="flex justify-end mt-4">
          <ExportTable
            onDownload={extension =>
              createExport(
                auth.data?.token || '',
                extension,
                createExportObject(
                  headerArr,
                  createExportRows(
                    uniqueServices,
                    ['m_BMODIF', 'm_RELNR', 'm_ClientID', 'm_DATUM', 'm_DTCREATION'],
                    { partners, customers, relnr: 'm_RELNR' }
                  )
                )
              )
            }
          />
        </div>
      )}

      <div className="my-4 border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader className="bg-slate-100">
            <TableRow>
              <TableHead className="uppercase">{t("sip.o_e2e.modified")}</TableHead>
              <TableHead className="uppercase min-w-[180px]">{t("sip.o_e2e.partner")}</TableHead>
              <TableHead className="uppercase min-w-[240px]">{t("sip.o_e2e.customer")}</TableHead>
              <TableHead className="uppercase">{t("sip.o_e2e.atm_id")}</TableHead>
              <TableHead className="uppercase">{t("sip.o_e2e.date")}</TableHead>
              <TableHead className="uppercase">{t("sip.o_e2e.creation_date")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {splitArrayIntoChunks(uniqueServices, 20)[currentPage - 1]?.map((service: any, index: number) => {

              const displayCustomer = 
                selectedCustomerId && 
                selectedCustomerId !== process.env.REACT_APP_RDM_NUM && 
                selectedCustomerId !== 'clear' &&
                String(service.m_RELNR) === String(selectedCustomerId) && 
                selectedCustomerName
                  ? (() => {
                      const parts = selectedCustomerName.split(' - ')
                      return parts.length >= 2 ? parts[1]?.trim() : selectedCustomerName.trim() || '-'
                    })()
                  : (() => {
                      const customer = customers
                        .flatMap(obj => obj.eindklant || [])
                        .find(e => String(e.relnr) === String(service.m_RELNR))
                      return customer?.name?.trim() || '-'
                    })()

              return (
                <TableRow 
                  key={index} 
                  className={`${index % 2 ? 'bg-slate-50' : ''} cursor-pointer`}
                  onClick={() => {
                    const url = `${service.m_ClientID}/${formatDateToYYYYMMDD(new Date(service.m_DATUM))}/${service.m_RELNR}`
                    navigate(url)
                  }}
                >
                  <TableCell>{service.m_BMODIF ? t("sip.o_e2e.yes") : t("sip.o_e2e.no")}</TableCell>
                  <TableCell className="min-w-[180px]">{service.lsPartner || '-'}</TableCell>
                  <TableCell className="min-w-[240px]">{displayCustomer}</TableCell>
                  <TableCell>{service.m_ClientID || '-'}</TableCell>
                  <TableCell>{service.m_DATUM ? format(new Date(service.m_DATUM), "dd/MM/yyyy") : '-'}</TableCell>
                  <TableCell>{service.m_DTCREATION ? format(new Date(service.m_DTCREATION), "dd/MM/yyyy") : '-'}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <Pagination
        totalItems={uniqueServices.length}
        itemsPerPage={20}
        page={currentPage}
        onPageChange={page => setcurrentPage(Number(page))}
      />
    </>
  )
}

export default E2ETable